import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useUrlLocalizer, useTranslator } from 'tradera-lang/translate';
import { useInView } from 'react-intersection-observer';
import useIsMobile from 'tradera-hooks/use-is-mobile';
import ALink from 'tradera-components/alink/alink';
import ItemCard from 'tradera-components/item-card/item-card';
import { lazyLoadSettings } from 'tradera-components/item-card/config';
import { generateItemCardPlaceholders } from 'tradera-components/item-card/generate-item-card-placeholders';
import itemActions from 'tradera-state/items/actions';
import uiActions from 'tradera-state/ui/actions';
import { trackContainerClick } from '../analytics';
import { fetchWishList } from 'tradera-state/views/start-page/actions';
import HeartFilledIcon from '@tradera/blueprint/theme/icons2/heart-fill.svg?jsx';
import WishListInfoCard from './wish-list-info-card';
import { localStore } from 'utils/storage';
import { STORAGE_KEYS } from 'tradera-constants/storage';
import 'tradera-components/item-card/layout.scss';
import useIsLoggedIn from 'tradera-hooks/use-is-loggedin';
import { useIsFeatureEnabled } from 'tradera-hooks/use-is-feature-enabled';

const WishList = () => {
    const isLoggedIn = useIsLoggedIn();
    const isWishListEnabled = useIsFeatureEnabled('startpage-wish-list');

    return isLoggedIn && isWishListEnabled ? <WishListInternal /> : null;
};

const WishListInternal = () => {
    const { t } = useTranslator();
    const dispatch = useDispatch();

    const [loadingTriggerRef, loadingTriggerInView] = useInView({
        triggerOnce: true
    });

    useEffect(() => {
        if (loadingTriggerInView) {
            dispatch(fetchWishList());
        }
    }, [dispatch, loadingTriggerInView]);

    const itemCount = 8;
    const { itemIds, status } = useSelector(
        state => state.views.startPage.wishList
    );
    const items = useSelector(state => state.items.cache);
    const isMobile = useIsMobile();

    const resolveItems = (itemIds = [], itemsMap = {}, maxItemsCount = 0) => {
        const mappedItems = itemIds.map(itemId => itemsMap[itemId]);
        return mappedItems.filter(item => !!item).slice(0, maxItemsCount);
    };

    const displayItems = ['NOT_FETCHED', 'LOADING'].includes(status)
        ? generateItemCardPlaceholders(itemCount)
        : resolveItems(itemIds, items, itemCount);

    const toggleWishList = itemId => {
        dispatch(itemActions.wishlist(itemId));
    };

    const openQuickView = itemId =>
        dispatch(
            uiActions.openQuickView({
                itemIds,
                itemId,
                openedFrom: 'Start Page Wish List'
            })
        );

    const { toLocalizedUrl } = useUrlLocalizer();

    const onCloseInfoCard = () => {
        setHideWishListInfoCard(true);
    };
    const [hideWishListInfoCard, setHideWishListInfoCard] = useState(
        localStore.getItem(STORAGE_KEYS.HIDE_WISH_LIST_INFO_CARD) || false
    );
    useEffect(
        () =>
            localStore.setItem(
                STORAGE_KEYS.HIDE_WISH_LIST_INFO_CARD,
                hideWishListInfoCard
            ),
        [hideWishListInfoCard]
    );

    const showInfoCard =
        isMobile && !hideWishListInfoCard && status === 'HAS_DATA';
    const showFallbackInfo = status === 'HAS_NO_DATA';

    return (
        <section className="px-0 pt-3" ref={loadingTriggerRef}>
            <h3 className="start-page-section-title">
                {t('myt_wishlist_title')}
            </h3>
            {showFallbackInfo ? (
                <div className="mb-2 mx-2 mx-md-3 mx-lg-2 mx-xl-1 mr-md-2 p-2 bg-light d-flex flex-column align-items-center">
                    <HeartFilledIcon className="icon icon-md icon-danger m-4" />
                    <p
                        className="mb-4"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{
                            __html: t('wishList_fallbackDescription', {
                                linkStart: `<a href="${toLocalizedUrl(
                                    '/my/wish-list'
                                )}">`,
                                linkEnd: '</a>'
                            })
                        }}
                    />
                </div>
            ) : (
                <>
                    <div
                        className={`item-card-layout d-flex ${
                            isMobile
                                ? 'layout-scroll horizontal-scroll flex-nowrap overflow-x-auto'
                                : 'layout-grid flex-wrap'
                        }`}>
                        {showInfoCard && (
                            <WishListInfoCard onClose={onCloseInfoCard} />
                        )}
                        {displayItems.map(item => (
                            <div
                                key={item.itemId}
                                className="item-card-container flex-shrink-0"
                                style={{
                                    transition: 'opacity 1000ms',
                                    opacity:
                                        !!item.itemUrl && !item.existsInWishList
                                            ? 0.2
                                            : 1
                                }}>
                                <ItemCard
                                    item={item}
                                    lazy={lazyLoadSettings}
                                    onAddToWishlist={toggleWishList}
                                    onQuickView={openQuickView}
                                    onRemoveFromWishlist={toggleWishList}
                                    ready={!!item.itemUrl}
                                    gaCategory="Startpage wish list"
                                    showBidStatus={true}
                                    showShippingOrOnDisplay={false}
                                />
                            </div>
                        ))}
                        {isMobile && (
                            <div
                                className="flex-shrink-0 px-2 pb-2 d-flex flex-column justify-content-center"
                                style={{
                                    marginBottom: '2rem'
                                }}>
                                <ALink
                                    className="btn btn-primary"
                                    href={toLocalizedUrl('/my/wish-list')}
                                    onClick={() =>
                                        trackContainerClick(
                                            'wishList',
                                            'Wish-List button below'
                                        )
                                    }
                                    useLink={false} // Use 'inSPA' when mytradera-page can be rnavigated to without a hard reload
                                >
                                    {`${t('wishList_showMore')} ->`}
                                </ALink>
                            </div>
                        )}
                    </div>
                    {!isMobile && (
                        <div className="row">
                            <div className="col-12 text-center">
                                <ALink
                                    className="btn btn-primary btn-wide"
                                    href={toLocalizedUrl('/my/wish-list')}
                                    onClick={() =>
                                        trackContainerClick(
                                            'wishList',
                                            'Wish-List button below'
                                        )
                                    }
                                    useLink={false} // Use 'inSPA' when mytradera-page can be rnavigated to without a hard reload
                                >
                                    {`${t('wishList_showMore')} ->`}
                                </ALink>
                            </div>
                        </div>
                    )}
                </>
            )}
        </section>
    );
};

export default WishList;
